import React, {useState, useEffect, lazy, Suspense, forwardRef, useRef} from "react"
import {IconContext} from "react-icons"
import {TbUsersGroup, TbBuildingBank, TbMessages} from "react-icons/tb"
import {RiCalendarEventFill} from "react-icons/ri"
import {LuPencilRuler} from "react-icons/lu"
import {useNavigate} from "react-router-dom"
import {useGetRequirementDashboard} from "../api/queries"
import Spinner from "@murphy-frontend/web-core/components/Spinner"
import {useUserPreferences} from "../../../common/contexts/UserPreferencesContext"
import CustomTooltip from "../../../common/CustomTooltip"
import { useCustomer } from "../../../common/contexts/CustomerContext"

const ResponsiveBar = lazy(() => import("@nivo/bar").then(module => ({default: module.ResponsiveBar})))

export interface RequirementDashboard {
	GoalLevel: number
	categoryList: categoryList[]
	averageScore: number
}

export interface categoryList {
	categoryId: number
	value: number
}

const maxValue = 4

function getCategoryColorAlpha(categoryId: number) {
	switch (categoryId) {
		case 1:
			return "rgba(0, 35, 149, 0.3)"
		case 2:
			return "rgba(210, 138, 200, 0.3)"
		case 3:
			return "rgba(0, 53, 81, 0.3)"
		case 4:
			return "rgba(255, 161, 0, 0.3)"
		case 5:
			return "rgba(0, 101, 189, 0.3)"
		default:
			return "rgba(204, 204, 204, 0.3)"
	}
}

function getCategoryColorFull(categoryId: number) {
	switch (categoryId) {
		case 1:
			return "#002395"
		case 2:
			return "#d28ac8"
		case 3:
			return "#003551"
		case 4:
			return "#ffa100"
		case 5:
			return "#0065bd"
		default:
			return "#ccc"
	}
}

interface CategoryChartProps {
	showExploreButton?: boolean
	showRecommendedButton?: boolean
	onExploreClick?: (categoryId: number) => void
	showGoalLevel?: boolean
	showPdfContent?: boolean
	customerId: string,
	onRemind?: boolean
}

const CategoryChart = forwardRef<HTMLDivElement, CategoryChartProps>(
	(
		{
			showExploreButton = true,
			showRecommendedButton = true,
			onExploreClick,
			showGoalLevel = true,
			showPdfContent = false,
			customerId,
			onRemind = false,
		},
		ref
	) => {
		const {translations} = useUserPreferences()
		const [activeTab, setActiveTab] = useState<"activity" | "monthly">("activity")
		const navigate = useNavigate()
		const tooltipRef = useRef(null);
		const exploreRef = useRef(null);
		const {customer} = useCustomer();

		const getCategoryName = (categoryId: number) => {
			switch (categoryId) {
				case 1:
					return translations.tmsPeople || "People"
				case 2:
					return translations.tmsOrganization || "Organization"
				case 3:
					return translations.tmsPlan || "Plan"
				case 4:
					return translations.tmsTool || "Tools"
				case 5:
					return translations.tmsCommunication || "Communication"
				default:
					return "Unknown"
			}
		}

		useEffect(() => {
			if (onRemind) {
				setTimeout(() => {
					exploreRef.current.focus();
					tooltipRef?.current?.openTooltip();
				}, 1000)
			}
		}, [onRemind])

		const {data: dashboard, isLoading, isPending} = useGetRequirementDashboard(customerId)

		if (isLoading || isPending) {
			return (
				<div className='spinner-container-global-center'>
					<Spinner />
				</div>
			)
		}

		const chartData = (dashboard?.categoryList || []).map(cat => ({
			categoryId: cat.categoryId,
			category: getCategoryName(cat.categoryId),
			value: cat.value
		}))

		const icon = (categoryId: number) => {
			switch (categoryId) {
				case 1:
					return <TbUsersGroup className='levelview-icon category-icon' />
				case 2:
					return <TbBuildingBank className='levelview-icon category-icon' />
				case 3:
					return <RiCalendarEventFill className='levelview-icon category-icon' />
				case 4:
					return <LuPencilRuler className='levelview-icon category-icon' />
				case 5:
					return <TbMessages className='levelview-icon category-icon' />
				default:
					return null
			}
		}

		const markers = [
			...(showGoalLevel && dashboard?.GoalLevel !== undefined
				? [
						{
							axis: "y",
							value: dashboard.GoalLevel,
							lineStyle: {stroke: "#ccc", strokeWidth: 2, strokeDasharray: "6,6"},
							legendPosition: "top-right",
							legendOffsetY: -6
						}
					]
				: []),
			{
				axis: "y",
				value: dashboard?.averageScore,
				lineStyle: {stroke: "#ccc", strokeWidth: 2},
				legend: `${translations.tmsAvg || "Avg"} (${dashboard.averageScore?.toFixed(2) ?? ""})`,
				legendPosition: "top-right",
				legendOffsetY: dashboard?.averageScore < dashboard?.GoalLevel ? -7 : 7
			}
		]

		const CategoryLayer = ({bars}) => {
			const minCategoryId = (() => {
				const nanBar = bars.find(bar => isNaN(Number(bar.data.data.value)))
				if (nanBar) return nanBar.data.data.categoryId
				let minValue = Infinity
				let minId = null
				bars.forEach(bar => {
					const val = Number(bar.data.data.value)
					if (val < minValue) {
						minValue = val
						minId = bar.data.data.categoryId
					}
				})
				return minId
			})()

			return (
				<>
					{bars.map(bar => {
						const categoryId = bar.data.data.categoryId
						const isMinBar = categoryId === minCategoryId
						const handleExploreClick = () => {
							if (onExploreClick) {
								onExploreClick(categoryId)
							} else {
								navigate(`/tmscategory/${customerId}/${categoryId}`)
							}
						}

						return (
							<foreignObject
								key={bar.key}
								x={bar.x}
								y={bar.y + bar.height + 5}
								width={bar.width}
								height={100}
								style={{overflow: "visible"}}
							>
								<div className='category-layer'>
									<IconContext.Provider value={{}}>{icon(categoryId)}</IconContext.Provider>
									<div className='category-label'>{getCategoryName(categoryId)}</div>
									{!showPdfContent && showExploreButton && (
										<button ref={categoryId === 2 ? exploreRef : null} className='explore-button' onClick={handleExploreClick} data-tooltip-id={categoryId === 2 ? "recommended" : null}>
											{translations.tmsExplor || "Explore"}
										</button>
									)}
									{!showPdfContent && showRecommendedButton && isMinBar && (
										<button className='recommended-button' style={{marginTop: "4px"}}>
											{translations.tmsRecommend || "Recommended"}
										</button>
									)}
								</div>
							</foreignObject>
						)
					})}
				</>
			)
		}

		const TopColorLayer = ({bars}) => {
			const TOP_RECT_HEIGHT = 4
			return (
				<>
					{bars.map(bar => {
						const categoryId = Number(bar.data.data.categoryId)
						const fullColor = getCategoryColorFull(categoryId)
						const rectHeight = Math.min(bar.height, TOP_RECT_HEIGHT)
						return (
							<rect
								key={bar.key + "-top"}
								x={bar.x}
								y={bar.y}
								width={bar.width}
								height={rectHeight}
								fill={fullColor}
							/>
						)
					})}
				</>
			)
		}

		let gridYValues = [0, 1, 2, 3, 4]

		return (
			<div
				ref={ref}
				className='category-chart'
				style={{
					width: "100%", // Ensure full width
					maxWidth: "1200px", // Prevent overriding parent maxWidth
					margin: "0 auto", // Center horizontally
					padding: "20px", // Match the original padding from tmsAssessmentResult
					boxSizing: "border-box" // Include padding in width/height calculations
				}}
			>
				<div className='dashboard__chart-container'>
					<Suspense fallback={<Spinner />}>
						<ResponsiveBar
							data={chartData}
							keys={["value"]}
							indexBy='category'
							margin={{top: 40, right: 40, bottom: 160, left: 60}}
							padding={0.6}
							valueScale={{type: "linear", max: 4}}
							indexScale={{type: "band", round: true}}
							enableLabel={false}
							colors={bar => getCategoryColorAlpha(bar.data.categoryId)}
							maxValue={maxValue}
							borderWidth={0}
							borderColor='none'
							theme={{
								background: "#fff",
								axis: {
									domain: {line: {stroke: "#ccc", strokeWidth: 1.5}},
									ticks: {line: {stroke: "#ccc", strokeWidth: 1.5}}
								},
								grid: {line: {stroke: "#eee", strokeWidth: 1.5, strokeDasharray: "4,4"}}
							}}
							axisBottom={null}
							gridYValues={gridYValues}
							axisLeft={{
								tickValues: gridYValues,
								tickSize: 0,
								tickPadding: 10,
								tickRotation: 0,
								legend: "",
								legendPosition: "middle",
								legendOffset: -40,
								format: value => value
							}}
							markers={markers}
							layers={["grid", "axes", "bars", "markers", TopColorLayer, "legends", CategoryLayer]}
						/>
					</Suspense>
				</div>
				{customer.IsFreemium && <CustomTooltip
					id={'recommended'}
					ref={tooltipRef}
					place={'bottom'}
					title={'Start here!'}
					body1={'This area has the lowest current readiness score.'}
					body2={'Begin your assessment here to make the biggest impact fast.'}
					buttons={[
						{ text: 'Go to Organization', type: 'button', onClick: () => {navigate(`/tmscategory/${customerId}/2`)}}
					]}
				/>}
			</div>
		)
	}
)

export default CategoryChart
